import Accordion from "@/common/components/Accordion";
import Button from "@/common/components/Button";
import { CopyButton } from "@/common/components/CopyButton";
import ResumeBox from "@/common/components/ResumeBox";
import { useCopyButton } from "@/common/hooks/useCopyButton"; import { formatDate } from "@/common/utils/formatters";
import useGetClientUserExternalToken from "@/features/privateCompanies/hooks/users/useGetClientUserExternalToken";
import { ClientUserExternalTokensData } from "@/features/privateCompanies/types/users/clientUserExternalTokens.type";
import { EyeIcon, EyeOffIcon } from "lucide-react";
import { useState } from "react";

interface ClientUserTokenItemProps {
  token: ClientUserExternalTokensData[number];
  displayIndex: number;
}

const ClientUserTokenItem = ({ token, displayIndex }: ClientUserTokenItemProps) => {
  const [secretAccessKey, setSecretAccessKey] = useState<string>();
  const [showSecretAccessKey, setShowSecretAccessKey] = useState(false);

  const {
    control: controlSecretAccessKey,
    copySuccess: copySecretAccessKeySuccess,
    copyFailed: copySecretAccessKeyFailed,
  } = useCopyButton();

  const {
    control: controlAccessKeyId,
    copySuccess: copyAccessKeyIdSuccess,
    copyFailed: copyAccessKeyIdFailed,
  } = useCopyButton();

  const {
    mutateAsync: getClientUserExternalToken,
  } = useGetClientUserExternalToken({
    tokenId: token.id,
  });

  const handleShowHideButtonClick = async () => {
    if (secretAccessKey) {
      setShowSecretAccessKey(!showSecretAccessKey);
    } else {
      const tokenData = await getClientUserExternalToken();

      if (tokenData.data.data.secret_access_key) {
        setSecretAccessKey(tokenData.data.data.secret_access_key);

        setShowSecretAccessKey(!showSecretAccessKey);
      }
    }
  }

  const handleCopySecretAccessKeyButtonClick = async () => {
    if (secretAccessKey) {
      copySecretAccessKeySuccess(secretAccessKey);
    } else {
      const tokenData = await getClientUserExternalToken();

      if (tokenData.data.data.secret_access_key) {
        setSecretAccessKey(tokenData.data.data.secret_access_key);

        copySecretAccessKeySuccess(tokenData.data.data.secret_access_key);
      } else {
        copySecretAccessKeyFailed();
      }
    }
  }

  const handleCopyAccessKeyIdButtonClick = () => {
    if (token.access_key_id) {
      copyAccessKeyIdSuccess(token.access_key_id);
    } else {
      copyAccessKeyIdFailed();
    }
  }

  return (
    <Accordion
      name={`user_external_token_${displayIndex}`}
      head={`Token - ${displayIndex}`}
      className="py-0"
      defaultOpen
      body={(
        <div className="flex flex-col gap-3">
          <ResumeBox
            label="Secret access key"
            valueClassName="overflow-hidden"
            value={(
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium flex-1 " data-testid="secret_access_key">
                  {showSecretAccessKey && secretAccessKey ? secretAccessKey : "".padEnd(36, "*")}
                </p>

                <Button
                  variant="link-white"
                  name="show_secret_access_key"
                  onClick={handleShowHideButtonClick}
                  operationKlassToCheckPermission="Admin::Client::UserExternalTokens::Show"
                >
                  {!showSecretAccessKey
                    ? <EyeIcon strokeWidth={1.5} className="size-5" />
                    : <EyeOffIcon strokeWidth={1.5} className="size-5" />}
                </Button>

                <Button
                  variant="link-white"
                  name="copy_secret_access_key"
                  onClick={handleCopySecretAccessKeyButtonClick}
                  operationKlassToCheckPermission="Admin::Client::UserExternalTokens::Show"
                >
                  <CopyButton control={controlSecretAccessKey} className="text-blue-800 size-5" />
                </Button>
              </div>
            )}
          />

          <div className="flex items-center gap-3 flex-wrap">
            <ResumeBox
              boxClassName="flex-1"
              valueClassName="overflow-hidden"
              label="Access key id"
              value={(
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium flex-1" data-testid="access_key_id">
                    {token.access_key_id}
                  </p>

                  <Button
                    variant="link-white"
                    name="copy_access_key_id"
                    onClick={handleCopyAccessKeyIdButtonClick}
                  >
                    <CopyButton control={controlAccessKeyId} className="text-blue-800 size-5" />
                  </Button>
                </div>
              )}
            />

            <ResumeBox
              labelClassName="text-nowrap"
              valueClassName="text-nowrap"
              label="Data de criação"
              value={token.created_at ? formatDate(new Date(token.created_at)) : '--'}
              testId="token_created_at"

            />

            <ResumeBox
              labelClassName="text-nowrap"
              valueClassName="text-nowrap"
              label="Data de expiração"
              value={token.expires_at ? formatDate(new Date(token.expires_at)) : '--'}
              testId="token_expires_at"
            />
          </div>
        </div>
      )}
    />
  );
}

export default ClientUserTokenItem;
